// src/pages/Marketing/MarketingI18nContext.js
//
// A small, DEDICATED i18n context for the marketing site — deliberately not
// a reuse of the tenant app's own I18nContext (that one drags in tenant-only
// API calls/permission machinery that doesn't exist for an anonymous
// visitor on posflect.com — see this feature's own plan doc's Grounding
// section and MarketingApp.js's own doc comment on why the whole Marketing
// tree stays isolated from both TenantApp and PlatformApp). Fetches the two
// public marketing endpoints directly (getPublicMarketingLanguages,
// getPublicMarketingContent) and re-fetches content whenever the visitor
// switches language, flipping document.documentElement.dir to match.
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { getPublicMarketingContent, getPublicMarketingLanguages } from "../../services/api";

const MarketingI18nContext = createContext(null);

export function MarketingI18nProvider({ children }) {
  const [languages, setLanguages] = useState([]);
  const [languageCode, setLanguageCode] = useState(null);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadContent = useCallback(async (code) => {
    try {
      const data = await getPublicMarketingContent(code);
      setContent(data);
    } catch (err) {
      setError(err.message || "Failed to load marketing content");
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const langs = await getPublicMarketingLanguages();
        setLanguages(langs || []);
        const defaultLang = (langs || []).find((l) => l.is_default) || langs?.[0];
        const code = defaultLang?.code || null;
        setLanguageCode(code);
        await loadContent(code);
      } catch (err) {
        setError(err.message || "Failed to load the marketing site");
      } finally {
        setLoading(false);
      }
    })();
  }, [loadContent]);

  useEffect(() => {
    const lang = languages.find((l) => l.code === languageCode);
    document.documentElement.dir = lang?.direction === "rtl" ? "rtl" : "ltr";
    document.documentElement.lang = languageCode || "en";
  }, [languageCode, languages]);

  const switchLanguage = useCallback(async (code) => {
    setLanguageCode(code);
    setLoading(true);
    await loadContent(code);
    setLoading(false);
  }, [loadContent]);

  return (
    <MarketingI18nContext.Provider value={{ languages, languageCode, content, loading, error, switchLanguage }}>
      {children}
    </MarketingI18nContext.Provider>
  );
}

export function useMarketingI18n() {
  const ctx = useContext(MarketingI18nContext);
  if (!ctx) throw new Error("useMarketingI18n must be used within a MarketingI18nProvider");
  return ctx;
}
